import { Link } from 'react-router-dom';
import { Badge, Card, Spinner } from '../ui';
import { useClosureTrail } from '../../hooks/useClosureTrail';
import { REGISTERS, CLOSURE_CONFIG } from '../../config/registers';
import { SHAREPOINT_SITE_DISPLAY_URL } from '../../config/sharepoint';
import { isLiveRef, parseRef } from '../../lib/recordQuery';
import type {
  ClosureLink,
  ClosureTargetType,
  LinkStatus,
} from '../../types/qms';
import styles from './ClosureTrail.module.css';

/**
 * ClosureTrail (04-08) — the differentiator over Centro (parity test #4) on the
 * admin surface: given a lodged incident, show its consequences (CI item,
 * care-plan revision, …) and each one's status in ONE place.
 *
 * Reads the live `Closure Links` graph via `useClosureTrail` (04-02) and renders
 * the `harmony.incidents.closure-trail` contract (02-04) EXACTLY as the Portal's
 * `renderIncidentsClosure()` does — each consequence as
 * `relationship → targetRef targetTitle targetStatus [linkStatus]`, ordered as the
 * service returns them (Created asc), with a "ready to close" badge when the
 * 02-03 close-readiness rule is met (`data.readyToClose`, computed via
 * `computeReadyToClose`). The two surfaces tell the same story.
 *
 * Record-level deep links for the source incident AND each consequence target
 * (the SharePoint DispForm, not a landing page — memory
 * `feedback_deep_links_in_briefs`). An expandable `<details>` disclosure mirrors
 * the Portal idiom; honest empty state against today's empty graph.
 *
 * 13-25: a source or target ref on one of the five registers also opens the dashboard's record page
 * (`/record/<ref>`); the SharePoint link stays beside it.
 *
 * PRESENTATION-ONLY — read-only render, no writes and no closure/sign-off
 * actions. Closure stays a human act in the system of record (authority floor
 * §2). This component never mutates the graph.
 */

// ---------------------------------------------------------------------------
// Deep-link builder — record-level SharePoint DispForm (reuses RegisterTable's
// recordUrl idiom, generalized to resolve a composed ref by its target type).
// ---------------------------------------------------------------------------

/**
 * Map a closure `TargetType` (02-04) to the SharePoint list TITLE that holds it,
 * so a consequence's `targetRef` (e.g. `CI-7`) deep-links to its own record.
 * Drawn from the single REGISTERS / CLOSURE_CONFIG source — never hard-coded.
 */
function listTitleForTargetType(targetType: ClosureTargetType): string | null {
  switch (targetType) {
    case 'ContinuousImprovement':
      return REGISTERS.ci.listTitle;
    case 'Risk':
      return REGISTERS.risk.listTitle;
    case 'CarePlanRevision':
      return CLOSURE_CONFIG.carePlanRevisions.listTitle;
    default:
      // EmergencyPlan / HRSupport (v1.1 link types) have no resolvable list yet.
      return null;
  }
}

/** The Incidents list title — the source of a trail is always an incident. */
const INCIDENTS_LIST_TITLE = REGISTERS.incidents.listTitle;

/**
 * Build the record-level deep link from a composed ref (`<PREFIX>-<Id>`) and the
 * SharePoint list title that holds it. We parse the id out of the ref (ISS-008:
 * the calc Reference column is never read) and point at the DispForm by id.
 * Returns null when the ref can't be resolved (so the UI degrades to plain text).
 */
function dispFormUrl(listTitle: string | null, composedRef: string): string | null {
  if (!listTitle) return null;
  const dash = composedRef.lastIndexOf('-');
  if (dash < 0) return null;
  const idPart = composedRef.slice(dash + 1);
  const id = Number(idPart);
  if (!Number.isFinite(id) || id <= 0) return null;
  return `${SHAREPOINT_SITE_DISPLAY_URL}/Lists/${encodeURIComponent(
    listTitle,
  )}/DispForm.aspx?ID=${id}`;
}

// ---------------------------------------------------------------------------
// Link-status → Badge tone (mirrors RegisterTable's status→tone vocabulary)
// ---------------------------------------------------------------------------

function linkStatusTone(
  linkStatus: LinkStatus,
): 'success' | 'warning' | 'neutral' | 'info' {
  switch (linkStatus) {
    case 'Complete':
      return 'success';
    case 'Cancelled':
      return 'neutral';
    case 'Open':
      return 'warning';
    default:
      return 'info';
  }
}

// ---------------------------------------------------------------------------
// One consequence row — `relationship → targetRef targetTitle targetStatus
// [linkStatus]` (the Portal's exact vocabulary).
// ---------------------------------------------------------------------------

function ConsequenceRow({ c }: { c: ClosureLink }) {
  const targetUrl = dispFormUrl(listTitleForTargetType(c.targetType), c.targetRef);

  return (
    <li className={styles.consequence}>
      <span className={styles.relationship}>{c.relationship || 'linked'}</span>
      <span className={styles.arrow} aria-hidden="true">
        →
      </span>
      {isLiveRef(c.targetRef) ? (
        <Link className={styles.targetRef} to={`/record/${parseRef(c.targetRef).ref}`} aria-label={`Open the record ${c.targetRef}`}>
          {c.targetRef}
        </Link>
      ) : (
        <span className={styles.targetRef}>{c.targetRef}</span>
      )}
      {targetUrl && (
        <a
          className={styles.targetTitle}
          href={targetUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open ${c.targetRef} in SharePoint`}
        >
          ↗
        </a>
      )}
      {c.targetTitle && (
        <span className={styles.targetTitle}>{c.targetTitle}</span>
      )}
      {c.targetStatus && (
        <span className={styles.targetStatus}>{c.targetStatus}</span>
      )}
      <Badge variant={linkStatusTone(c.linkStatus)} size="sm">
        {c.linkStatus}
      </Badge>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface ClosureTrailProps {
  /** The source record's composed reference, e.g. `INC-0042` (ISS-008). */
  sourceRef: string | null;
}

export default function ClosureTrail({ sourceRef }: ClosureTrailProps) {
  const { data, isLoading, error } = useClosureTrail(sourceRef);

  if (!sourceRef) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={styles.loading} role="status" aria-live="polite">
        <Spinner size="lg" />
        <span className={styles.loadingText}>Tracing this report…</span>
      </div>
    );
  }

  if (error) {
    return (
      <p role="alert" className={styles.error}>
        Couldn’t load the closure trail from the Quality &amp; Safety site: {error}
      </p>
    );
  }

  const consequences = data?.consequences ?? [];
  const readyToClose = data?.readyToClose ?? false;
  const sourceUrl = dispFormUrl(INCIDENTS_LIST_TITLE, sourceRef);

  return (
    <Card variant="elevated" padding="lg" className={styles.card}>
      {/* ── Source incident header + record-level deep link ── */}
      <header className={styles.header}>
        <div className={styles.headerMain}>
          <span className={styles.label}>Report</span>
          {isLiveRef(sourceRef) ? (
            <Link className={styles.sourceRef} to={`/record/${parseRef(sourceRef).ref}`} aria-label={`Open the record ${sourceRef}`}>
              {sourceRef}
            </Link>
          ) : (
            <span className={styles.sourceRef}>{sourceRef}</span>
          )}
          {sourceUrl && (
            <a
              className={styles.label}
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open ${sourceRef} in SharePoint`}
            >
              SharePoint ↗
            </a>
          )}
          {data?.closureStatus && (
            <Badge variant="info" size="sm">
              {data.closureStatus}
            </Badge>
          )}
        </div>
        {readyToClose && (
          <Badge variant="success" size="md">
            Ready to close
          </Badge>
        )}
      </header>

      {/* ── The consequence trail (expandable, mirrors the Portal) ── */}
      <details className={styles.disclosure} open>
        <summary className={styles.summary}>
          <span className={styles.summaryText}>
            Where this report ended up
          </span>
          <span className={styles.count}>
            {consequences.length === 0
              ? 'no links yet'
              : consequences.length === 1
                ? '1 consequence'
                : `${consequences.length} consequences`}
          </span>
        </summary>

        <div className={styles.body}>
          {consequences.length === 0 ? (
            <p className={styles.empty}>
              No linked consequences yet. As this report spawns improvements,
              care-plan revisions or other follow-ups, they’ll trace here.
            </p>
          ) : (
            <ul className={styles.trailList}>
              {consequences.map((c) => (
                <ConsequenceRow key={c.linkRef} c={c} />
              ))}
            </ul>
          )}
        </div>
      </details>
    </Card>
  );
}
